import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "components/ui/toast";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

// The file goes straight to storage, so a 2 GB video never passes through the backend.
const uploadFile = async (file, onProgress) => {
  const { data } = await api.post(API_ROUTES.KNOWLEDGE_UPLOAD_URL, {
    name: file.name,
    type: file.type,
    size: file.size,
  });
  const { key, url, contentType } = data.data;
  await api.put(url, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
  });
  return key;
};

export const useGetKnowledgeDocumentsQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.KNOWLEDGE],
    queryFn: () => api.get(API_ROUTES.KNOWLEDGE),
    // Reading a video takes minutes; keep checking until nothing is being read.
    refetchInterval: (query) =>
      query.state.data?.data?.data?.some((document) => document.status === "processing") ? 5000 : false,
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useGetKnowledgeDocumentQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.KNOWLEDGE, { id }],
    queryFn: () => api.get(`${API_ROUTES.KNOWLEDGE}/${id}`),
    enabled: !!id,
  });
  return { data: data?.data?.data ?? null, isPending, isError, error };
};

export const useAddKnowledgeDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, title, area, networks, onProgress }) => {
      const key = await uploadFile(file, onProgress);
      return api.post(API_ROUTES.KNOWLEDGE, { key, name: file.name, title, area, networks });
    },
    onSuccess: () => {
      showToast("success", "Added. Pilot can use it once it has been read.");
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.KNOWLEDGE] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to add the document")),
  });
};

export const useUpdateKnowledgeDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file, onProgress, ...changes }) => {
      if (file) {
        changes.key = await uploadFile(file, onProgress);
        changes.name = file.name;
      }
      return api.put(`${API_ROUTES.KNOWLEDGE}/${id}`, changes);
    },
    onSuccess: () => {
      showToast("success", "Document updated");
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.KNOWLEDGE] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to update the document")),
  });
};

export const useReprocessKnowledgeDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`${API_ROUTES.KNOWLEDGE}/${id}/reprocess`),
    onSuccess: () => {
      showToast("success", "Reading it again");
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.KNOWLEDGE] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to read it again")),
  });
};

export const useDeleteKnowledgeDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`${API_ROUTES.KNOWLEDGE}/${id}`),
    onSuccess: () => {
      showToast("success", "Document deleted");
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.KNOWLEDGE] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to delete the document")),
  });
};

export const useSearchKnowledgeMutation = () =>
  useMutation({
    mutationFn: async ({ question, networkId }) => {
      const { data } = await api.post(API_ROUTES.KNOWLEDGE_SEARCH, { question, networkId: networkId || undefined });
      return data.data;
    },
    onError: (err) => showToast("error", errorMessage(err, "Search failed")),
  });
